package ai.beakman.undercover

import kotlin.random.Random

/** Chaves de storage (compartilhadas pelos actuals de cada plataforma). */
internal const val IDENTITY_CLIENT_ID_KEY = "undercover.clientId"
internal const val IDENTITY_PLAYER_NAME_KEY = "undercover.playerName"
internal const val IDENTITY_LANGUAGE_KEY = "undercover.language"

/**
 * Identidade do device para o modo online, persistente entre sessões.
 *
 * - `clientId`: carregado do storage da plataforma; se não existir, é gerado
 *   (20 chars [a-z0-9]) e persistido. Se a plataforma não tiver storage disponível
 *   (ex.: Android sem `Identity.init(context)`, Safari em modo privado), o fallback
 *   é o comportamento antigo: identidade por sessão.
 * - `savedName` / `saveName`: nome do jogador salvo no device (pré-preenche a UI).
 */
object Identity {
    private const val CHARS = "abcdefghijklmnopqrstuvwxyz0123456789"
    private const val ID_LENGTH = 20

    val clientId: String by lazy {
        loadPersistedClientId() ?: generateClientId().also(::persistClientId)
    }

    /** Nome do jogador salvo no device, se houver. */
    val savedName: String?
        get() = loadSavedName()

    /** Persiste o nome do jogador para as próximas sessões. */
    fun saveName(name: String) = persistSavedName(name)

    /** Código de idioma persistido (ex.: "pt"), se houver. */
    fun loadLanguage(): String? = loadPersistedLanguage()

    /** Persiste o código de idioma escolhido para as próximas sessões. */
    fun persistLanguage(code: String) = persistLanguageCode(code)

    private fun generateClientId(): String =
        buildString { repeat(ID_LENGTH) { append(CHARS[Random.nextInt(CHARS.length)]) } }
}

internal expect fun loadPersistedClientId(): String?
internal expect fun persistClientId(id: String)
internal expect fun loadSavedName(): String?
internal expect fun persistSavedName(name: String)
internal expect fun loadPersistedLanguage(): String?
internal expect fun persistLanguageCode(code: String)
